import { Modal, Select, SelectOptionType } from '@bp/ui-components';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue } from 'react-select';
import { ModalBottomButtons } from '../ModalBottomButtons/ModalBottomButtons';

type DuplicateConditionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Localised title (whole conditions vs. single condition). */
  title: string;
  /** Localised hint sentence (already interpolated with the source name). */
  hint: string;
  /** Selectable target resources (the source itself is expected to be excluded by the caller). */
  options: SelectOptionType[];
  isLoading?: boolean;
  onConfirm: (targetUuids: string[]) => void;
};

/**
 * Target picker for copying conditions onto other resources of the same type — the conditions
 * counterpart of {@link DuplicateAvailabilityModal}. Title/hint are passed in so it serves both the
 * "all conditions" (action bar) and "single condition" (per-item) flows.
 */
export const DuplicateConditionsModal: FC<DuplicateConditionsModalProps> = ({
  isOpen,
  onClose,
  title,
  hint,
  options,
  isLoading,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<SelectOptionType[]>([]);

  const handleClose = () => {
    setSelected([]);
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(selected.map((option) => String(option.value)));
    setSelected([]);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      title={title}
      width='s'
      hideFooter={false}
      footer={
        <ModalBottomButtons
          isLoading={isLoading}
          closeButton={{ callback: handleClose }}
          submitButton={{
            text: t('conditions.duplicateModal.submit'),
            callback: handleConfirm,
            disabled: selected.length === 0 || isLoading,
          }}
        />
      }
    >
      <p>{hint}</p>
      <Select
        menuPosition='fixed'
        isMulti
        isSearchable
        options={options}
        value={selected}
        onChange={(option) => setSelected([...(option as MultiValue<SelectOptionType>)])}
        name='duplicate-conditions-targets'
        label={t('conditions.duplicateModal.targetsLabel')}
      />
    </Modal>
  );
};
